import {inject, Injectable} from '@angular/core';
import {OidcFlow, OidcFlowConfig} from '@assets/OidcFlow.v2';
import {OidcConfigProvider} from '@oidc/OidcConfigProvider';
import {ActorId, OutletPeer} from '@service-and-repairs/awpintegrationlib';
import {BehaviorSubject, firstValueFrom, map, Observable, Subject} from 'rxjs';
import {AwpCookieService} from '../../../auth/awp-cookie.service';
import {CsslToken} from '../../../auth/cssl/cssl-token';
import {LogoutBroadcastService} from '../../../auth/logout-broadcast.service';
import {OidcToken} from '../../../auth/oidc/oidc-token';
import {AwpClientLibService} from '../../../services/awp-client-lib.service';
import {Util} from '../../../util/util';
import {UserSettings} from '../interfaces/user-settings';
import {UserData} from '../models/user-data';
import {OutletPeerScope, UserApiService} from './user-api.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly awpClientLibService: AwpClientLibService = inject(AwpClientLibService);
  private readonly userApiService: UserApiService = inject(UserApiService);
  private readonly awpCookieService: AwpCookieService = inject(AwpCookieService);
  private readonly logoutBroadcastService: LogoutBroadcastService = inject(LogoutBroadcastService);

  private readonly userSubject: BehaviorSubject<UserData | null> = new BehaviorSubject<UserData | null>(null);
  readonly user$: Observable<UserData | null> = this.userSubject.asObservable()
    .pipe(map((user: UserData | null): UserData | null => UserData.copy(user)));
  readonly loggedOut: Subject<void> = new Subject<void>();

  constructor() {
    // Log out this tab when the user logs out in another tab of the same origin.
    this.logoutBroadcastService.logoutReceived.subscribe((): void => this.handleLogoutFromOtherTab());
  }

  private static getEndSessionUrl(oidcConfig: OidcFlowConfig, idToken: string): string {
    const url: URL = new URL(
      `https://${oidcConfig.wenEnvironment}/auth/oauth2/realms/root/realms/${oidcConfig.realm}/connect/endSession`
    );
    url.searchParams.set('id_token_hint', idToken);
    url.searchParams.set('post_logout_redirect_uri', window.location.origin);
    return url.href;
  }

  private static endSession(oidcConfig: OidcFlowConfig, idToken: string): void {
    window.location.href = UserService.getEndSessionUrl(oidcConfig, idToken);
  }

  /**
   * WEN's (alpha realm) end-session endpoint hard-requires a well-formed `id_token_hint` JWT; a
   * missing or malformed value makes it respond with a raw JSON `bad_request` page ("source code
   * error message") instead of redirecting back to the login screen (see ISPI-486175). This guard
   * ensures the end-session endpoint is only ever called with a structurally valid JWT.
   */
  private static isValidIdTokenHint(idToken: string | undefined): boolean {
    return !!idToken && /^[A-Za-z0-9_=-]+\.[A-Za-z0-9_=-]+\.[A-Za-z0-9_=-]+$/.test(idToken);
  }

  /**
   * Fallback used when no valid `id_token_hint` is available for the WEN end-session request:
   * reloads the application origin so the user lands on the login screen instead of WEN's raw
   * error page.
   */
  private static redirectToOrigin(): void {
    window.location.href = `${window.location.protocol}//${window.location.hostname}`;
  }

  getOutletPeers(outletPeerScope?: OutletPeerScope): Promise<OutletPeer[]> {
    return firstValueFrom(this.userApiService.getOutletPeers(outletPeerScope)).catch((error: unknown): OutletPeer[] => {
      console.log('[WebApp] [UserService] Could not get outlet peers', error);
      return [];
    });
  }

  async setUserSettings(updatedUserSettings: UserSettings): Promise<void> {
    await firstValueFrom(this.userApiService.setUserSettings(updatedUserSettings));
  }

  deletePersonalData(): Observable<unknown> {
    this.userApiService.deleteColumnsSettings().subscribe({
      error: (error: unknown): void => console.warn(this.logPrefix() + 'Could not delete column settings.', error)
    });
    return this.userApiService.deleteUserSettings();
  }

  getCurrentUser(): UserData | null {
    return UserData.copy(this.userSubject.getValue());
  }

  updateUser(user: UserData | null): void {
    this.userSubject.next(UserData.copy(user));
  }

  publishUser(user: UserData, destination?: ActorId): void {
    this.updateUser(user);
    this.awpClientLibService.serverApi.publishActiveUser(UserData.copy(user), destination);
  }

  logOut(): void {
    console.log(this.logPrefix() + 'Log out user.');
    this.loggedOut.next();
    this.logoutBroadcastService.broadcastLogout();
    this.clearLocalAuthStateAndEndSession();
  }

  /**
   * Logs out the user and redirects to the given path after re-login.
   * The redirectPath (including query params) will be preserved across the logout/login cycle
   * via local storage.
   */
  logOutWithRedirect(redirectPath: string): void {
    console.log(this.logPrefix() + 'Log out user with redirect to: ' + redirectPath);
    this.loggedOut.next();
    this.logoutBroadcastService.broadcastLogout();
    this.clearLocalAuthStateAndEndSession(redirectPath);
  }

  /**
   * Removes this tab's authentication tokens from session storage and ends the WEN session.
   * When a redirectPath is given, it is preserved across the logout/login cycle so the user
   * returns to it after re-login.
   */
  private clearLocalAuthStateAndEndSession(redirectPath?: string): void {
    const config = (new OidcConfigProvider()).get(window.location.hostname);
    const oidcFlow = new OidcFlow(config);
    if (!oidcFlow.oidcIsSupported()) {
      return;
    }

    const businessContext = this.userSubject.getValue()?.getContext();
    const oidcToken = OidcToken.readFromStorage(businessContext);
    const csslToken = CsslToken.readFromStorage(businessContext);

    this.deleteAwpCookieThenFinalizeLogout(oidcToken, (): void => {
      if (csslToken?.jwt) {
        csslToken.deleteFromStorage();
      }

      oidcToken?.deleteFromStorage();
      sessionStorage.removeItem('cavors-session-id');

      if (UserService.isValidIdTokenHint(oidcToken?.id_token)) {
        if (redirectPath !== undefined) {
          UserService.endSessionWithRedirect(config, oidcToken.id_token, redirectPath);
        } else {
          UserService.endSession(config, oidcToken.id_token);
        }
      } else if (oidcToken?.access_token) {
        console.warn(this.logPrefix() + 'No valid OIDC id_token available for the WEN end-session request; '
          + 'redirecting to the login screen instead of calling the end-session endpoint.');
        if (redirectPath !== undefined) {
          localStorage.setItem('awp-post-login-redirect', redirectPath);
        }
        UserService.redirectToOrigin();
      }
    });
  }

  /**
   * Performs best-effort awp_cookie deletion before clearing local auth state and redirecting.
   */
  private deleteAwpCookieThenFinalizeLogout(oidcToken: OidcToken, finalizeLogout: () => void): void {
    console.info(this.logPrefix() + 'Deleting awp_cookie before clearing local auth state.');
    this.awpCookieService.deleteCookie(oidcToken?.access_token)
      .then((): void => {
        console.info(this.logPrefix() + 'Successfully deleted awp_cookie before logout finalization.');
      })
      .catch((error: unknown): void => {
        console.warn(this.logPrefix() + 'Failed to delete awp_cookie during logout.', error);
      })
      .finally((): void => {
        console.debug(this.logPrefix() + 'Continuing logout flow after awp_cookie delete attempt.');
        finalizeLogout();
      });
  }

  private static endSessionWithRedirect(oidcConfig: OidcFlowConfig, idToken: string, redirectPath: string): void {
    // Store the redirect path in localStorage so it survives the external redirect to WEN and back
    localStorage.setItem('awp-post-login-redirect', redirectPath);
    UserService.endSession(oidcConfig, idToken);
  }


  /**
   * Handles a logout that was triggered in another tab. Because authentication tokens are stored
   * in this tab's own (isolated) session storage, this tab must clear its own tokens and end the
   * WEN session itself. The logout is intentionally not broadcast again to avoid a feedback loop
   * between tabs.
   */
  private handleLogoutFromOtherTab(): void {
    console.log(this.logPrefix() + 'Logging out because another tab logged out.');
    this.loggedOut.next();
    this.clearLocalAuthStateAndEndSession();
  }

  private logPrefix(): string {
    return Util.logPrefix('Webapp', 'User');
  }

  /**
   * Returns true if user is awp_b2d_dealeradmin.
   */
  userIsDealerAdmin(): boolean {
    const user: UserData = this.userSubject.getValue();
    return !!user && user.getRoles().includes('awp_b2d_dealeradmin');
  }
}
